import React, { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useSelector } from 'react-redux'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { selectIsOnline } from 'ducks/offline/selectors'
import useAiProviderStatus from './useAiProviderStatus'

export const RESTORED_VISIBLE_MS = 60000

export const AI_BANNER_COPY = {
  degraded: {
    pill: 'Guru paused: AI provider issues',
    title: 'Some AI features are paused',
    body: 'Our external AI provider is having some issues, so services such as Guru are not available right now. They should be back shortly. Everything else keeps working as normal.'
  },
  restored: {
    pill: 'AI features are back',
    title: 'AI features are back',
    body: 'Our AI provider is working again, so Guru and our other AI services are available.'
  }
}

const AiProviderBanner = ({ isLoggedIn }) => {
  const isOnline = useSelector(selectIsOnline)
  const { banner, dismissRestored } = useAiProviderStatus(Boolean(isLoggedIn) && isOnline !== false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (banner !== 'restored') return undefined
    const timer = setTimeout(dismissRestored, RESTORED_VISIBLE_MS)
    return () => clearTimeout(timer)
  }, [banner, dismissRestored])

  useEffect(() => {
    if (banner === 'hidden') setOpen(false)
  }, [banner])

  if (banner === 'hidden') return null

  const restored = banner === 'restored'
  const copy = AI_BANNER_COPY[banner]
  const Icon = restored ? CloudDoneOutlinedIcon : CloudOffIcon

  return (
    <Wrap data-testid='ai-provider-banner' role='status' aria-live='polite'>
      {open && (
        <Details id='ai-provider-details'>
          <DetailsTitle>{copy.title}</DetailsTitle>
          <DetailsBody>{copy.body}</DetailsBody>
        </Details>
      )}
      <Row>
        <Capsule
          type='button'
          $restored={restored}
          onClick={() => setOpen(value => !value)}
          aria-expanded={open}
          aria-controls='ai-provider-details'
          title={open ? 'Hide details' : 'Show details'}
        >
          <Icon aria-hidden='true' style={{ fontSize: 18 }} />
          <CapsuleText>{copy.pill}</CapsuleText>
        </Capsule>
        {restored && (
          <CloseButton type='button' onClick={dismissRestored} aria-label='Close this message'>
            <CloseRoundedIcon aria-hidden='true' style={{ fontSize: 18 }} />
          </CloseButton>
        )}
      </Row>
    </Wrap>
  )
}

export default AiProviderBanner

const slideUp = keyframes`
  from { transform: translateX(-50%) translateY(20px); opacity: 0; }
  to { transform: translateX(-50%) translateY(0); opacity: 1; }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Wrap = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: calc(100vw - 32px);
  animation: ${slideUp} 0.3s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 768px) {
    left: 16px;
    bottom: var(--bp-float-1);
    transform: none;
    align-items: flex-start;
    max-width: calc(100vw - 92px);
    animation: ${fadeIn} 0.3s ease-out;
  }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
`

const Capsule = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  max-width: 100%;
  padding: 10px 20px;
  border: 0;
  border-radius: 50px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
  user-select: none;
  transition: box-shadow 0.15s;

  ${({ $restored }) => ($restored
    ? css`background: ${RAG.low.fg};`
    : css`background: ${RAG.medium.fg};`)}

  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 8px 16px;
  }
`

const CapsuleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #334155;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const Details = styled.div`
  box-sizing: border-box;
  width: min(380px, calc(100vw - 32px));

  @media (max-width: 768px) {
    width: calc(100vw - 92px);
  }

  padding: 12px 16px;
  border-radius: 12px;
  background: #fff;
  color: #1f2937;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
`

const DetailsTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 4px;
`

const DetailsBody = styled.div`
  font-size: 14px;
  line-height: 1.45;
  color: #374151;
`
